import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, inr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUSES = ["new", "confirmed", "shipped", "delivered", "cancelled"];

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);

  const load = () =>
    api
      .get("/admin/orders")
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.id}`, { order_status: status });
      toast.success(`Order ${order.order_number} → ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-orders-tab">
      <h2 className="mb-6 font-display text-3xl">Orders</h2>
      {orders.length === 0 ? (
        <div className="border border-line bg-white p-12 text-center" data-testid="admin-orders-empty">
          <p className="font-display text-2xl">No orders yet.</p>
          <p className="mt-2 text-sm text-stone2">Customer orders and enquiries will land here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-line bg-white" data-testid="admin-orders-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`admin-order-row-${o.order_number}`}>
                  <TableCell>
                    <p className="font-mono text-xs font-semibold">{o.order_number}</p>
                    <p className="text-[11px] text-stone-400">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{o.customer.name}</p>
                    <p className="text-[11px] text-stone2">{o.customer.phone} · {o.customer.city}</p>
                  </TableCell>
                  <TableCell>{o.items.reduce((s, i) => s + i.qty, 0)}</TableCell>
                  <TableCell className="font-semibold">{inr(o.total)}</TableCell>
                  <TableCell>
                    <Badge variant={o.payment_status === "paid" ? "default" : "secondary"} className={o.payment_status === "paid" ? "bg-moss text-white" : ""} data-testid={`order-payment-badge-${o.order_number}`}>
                      {o.payment_method === "enquiry" ? "Enquiry" : o.payment_status === "paid" ? "Paid" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={o.order_status} onValueChange={(v) => setStatus(o, v)}>
                      <SelectTrigger className="h-8 w-36 border-stone-300 text-xs" data-testid={`order-status-select-${o.order_number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="text-xs capitalize">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
